import React from 'react';

export interface LinkProps {
  /**
   * Link URL
   */
  href: string;
  /**
   * Link text/content
   */
  children: React.ReactNode;
  /**
   * Remove underline (links are underlined by default)
   */
  noUnderline?: boolean;
  /**
   * External link (opens in new tab)
   */
  external?: boolean;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Click event handler
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Target attribute
   */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /**
   * Rel attribute
   */
  rel?: string;
  /**
   * HTML title attribute
   */
  title?: string;
  /**
   * HTML id attribute
   */
  id?: string;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * ARIA role
   */
  role?: string;
  /**
   * Tab index for keyboard navigation
   */
  tabIndex?: number;
  /**
   * ARIA label
   */
  'aria-label'?: string;
  /**
   * ARIA described by
   */
  'aria-describedby'?: string;
  /**
   * ARIA current (for navigation)
   */
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
}

export const Link: React.FC<LinkProps> = ({
  href,
  children,
  noUnderline = false,
  external = false,
  disabled = false,
  onClick,
  target,
  rel,
  title,
  id,
  className = '',
  style,
  role,
  tabIndex,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-current': ariaCurrent,
}) => {
  // Base class - includes underline by default
  const baseClass = 'hi-ui-link hi-ui-link-underline';

  // No underline class
  const noUnderlineClass = noUnderline ? 'hi-ui-link-no-underline' : '';

  // Disabled class
  const disabledClass = disabled ? 'hi-ui-link-disabled' : '';

  const allClasses = `${baseClass} ${noUnderlineClass} ${disabledClass} ${className}`.trim();

  // Handle external links
  const linkTarget = external ? '_blank' : target;
  const linkRel = external ? (rel ? `${rel} noopener noreferrer` : 'noopener noreferrer') : rel;

  // Handle disabled state
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <a
      href={disabled ? undefined : href}
      className={allClasses}
      onClick={handleClick}
      target={linkTarget}
      rel={linkRel}
      title={title}
      id={id}
      style={style}
      role={role}
      tabIndex={tabIndex !== undefined ? tabIndex : disabled ? -1 : undefined}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      aria-current={ariaCurrent}
      aria-disabled={disabled ? true : undefined}
    >
      {children}
    </a>
  );
};
